import BootImageModal from "@app/components/boot-image-modal";
import BootResultModal from "@app/components/boot-result-modal";
import ConfirmModal from "@app/components/confirm-modal";
import PlayPcrenderModal from "@app/components/play-pcrender-modal";
import { addToast } from "@app/components/toast/toast.service";
import { IPXoayModal, TunnelModal, WanModal } from "@app/components/wan-modal";
import QRCodeModal from "@app/components/wan-modal/qr-modal.component";
import {
  BootImageUnit,
  BootStatusCode,
  CURRENT_ACCOUNT,
  DEFAULT_PAGE,
  DEFAULT_TOTAL_PAGES,
  MAX_LEN,
  OrderStatus,
  PathnameUrl,
  SystemMessage,
} from "@app/constants";
import AccountService from "@app/services/http/account.service";
import CategoryService from "@app/services/http/category.service";
import ImageService from "@app/services/http/image.service";
import LogService from "@app/services/http/log.service";
import OrderService from "@app/services/http/order.service";
import PackageService from "@app/services/http/package.service";
import CloudPcService from "@app/services/http/cloud.pc.service";
import SshService from "@app/services/http/ssh.service";
import { openPortalDialog } from "@app/services/modal.service";
import SocketService from "@app/services/socket.service";
import { storeMyInfo } from "@app/store/my-info/my-info.action";
import {
  Account,
  Category,
  CreateOrderRequest,
  ImageItem,
  LogRequest,
  Order,
  Package,
  SshCli,
  Tunnel,
} from "@app/types";
import { bySortOrder, downloadFile, getOrderEndTime } from "@app/utils/util";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import HttpService from "@core/services/http/http.service";
import useBusy from "@core/hooks/use-busy.hook";
import StorageService from "@core/services/storage";
import { useEffect, useMemo, useRef, useState } from "react";
import { Subscription } from "rxjs";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfigCatalog from "./config-catalog";
import CreateSummary, { defaultGroup, isGroupAvailable, parsePackageGroup } from "./create-summary";
import RentedTable from "./rented-table";
import { categoryTones, packageTones, toneColor } from "./category-tone";

/** Máy đang thuê chỉ lấy đơn của máy chưa xoá (xoá máy là xoá mềm is_deleted: 1), cả máy vật lý và Cloud PC */
const RENTED_ALL = { active_image: 1 as const };

/**
 * Tab "Máy vật lý": logic lấy từ trang GpuHub, CreateImageModal và ComputerCard cũ.
 * Tab "Cloud PC" dùng chung component này với cloudPc = true (như bản thiết kế: cùng lưới thẻ, Thông tin tạo máy, Máy đang thuê):
 * chỉ hiện gói có cloud_pc; tạo máy, Reset và RDP gọi /cloud-pc; không có Đổi mật khẩu.
 */
function PhysicalTab({
  active = true,
  cloudPc = false,
  recreate,
  onRecreateOther,
}: {
  active?: boolean;
  cloudPc?: boolean;
  /** Đơn cần "Tạo lại máy" ở tab này (bấm từ tab kia); at để bấm lại cùng 1 đơn vẫn chạy */
  recreate?: { order: Order; at: number };
  /** Bấm "Tạo lại máy" ở đơn thuộc loại máy của tab kia: trang chuyển tab và điền ở tab đó */
  onRecreateOther?: (order: Order) => void;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Máy đang thuê: như bản thiết kế, cả 2 tab cùng hiện mọi máy (máy vật lý và Cloud PC)
  const RENTED_FILTER = RENTED_ALL;
  // gói thuộc tab này (gói Cloud PC chỉ hiện ở tab Cloud PC), theo thứ tự admin nhập (API đã xếp, xếp lại cho chắc)
  const ofTab = (items: Package[]) => items.filter((pkg) => !!pkg.cloud_pc === cloudPc).sort(bySortOrder);
  // 2 tab cùng được mount nên id của khung Máy đang thuê / Thông tin tạo máy phải khác nhau
  const rentedId = cloudPc ? "rented-cloud" : "rented";

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>();
  const [orderCount, setOrderCount] = useState({ busy: 0, finished: 0 });
  const [categories, setCategories] = useState<Category[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [packagesByCategory, setPackagesByCategory] = useState<
    Record<string, Package[]>
  >({});
  const [currentAccount, setCurrentAccount] = useState<Account>();
  // Đơn vừa đổi mật khẩu: dòng MK hiện mật khẩu mới, tô xanh trong giây lát như bản thiết kế
  const [pwNew, setPwNew] = useState("");
  const pwTimer = useRef<ReturnType<typeof setTimeout>>();

  // Màu danh mục theo package: thẻ cấu hình, Thông tin tạo máy, Máy đang thuê, Gia hạn dùng chung 1 màu
  const tones = useMemo(() => packageTones(categories, packagesByCategory), [categories, packagesByCategory]);
  // Máy đang thuê có cả máy của loại kia (gói không có trong tab này) nên lấy màu theo danh mục của chính gói
  const catTones = useMemo(() => categoryTones(categories), [categories]);
  const colorOf = (pkg?: Pick<Package, "_id" | "category">) => {
    if (!pkg) return toneColor(undefined);

    const category = pkg.category as unknown as string | { _id?: string } | undefined;
    const categoryId = typeof category === "object" ? category?._id : category;

    return toneColor(tones[pkg._id] || (categoryId ? catTones[categoryId] : undefined));
  };

  useEffect(() => () => clearTimeout(pwTimer.current), []);

  // Thông tin tạo máy
  const [selectedPackage, setSelectedPackage] = useState<Package>();
  // Bản mới nhất của gói đang chọn (is_available / available_groups được hỏi lại mỗi 5s),
  // để ô Hệ điều hành làm mờ đúng bản Windows vừa hết máy
  const livePackage = useMemo(
    () =>
      selectedPackage &&
      (Object.values(packagesByCategory)
        .flat()
        .find((item) => item._id === selectedPackage._id) ||
        packages.find((item) => item._id === selectedPackage._id) ||
        selectedPackage),
    [selectedPackage, packagesByCategory, packages]
  );
  const [imageName, setImageName] = useState("");
  const [group, setGroup] = useState("");
  const [unit, setUnit] = useState(BootImageUnit.HOUR);
  const [amount, setAmount] = useState(1);
  const [busy, setBusy] = useState(false);
  // thao tác trên từng máy đang thuê: chỉ nút đó xoay, không che cả trang (key "<thao tác>:<id máy>")
  const { busy: rowBusy, run } = useBusy();
  // Tích đồng ý không hoàn tiền + điều khoản (như Cloud Gaming), bấm Tạo mà chưa tích thì tô đỏ
  const [agree, setAgree] = useState(false);
  const [agreeError, setAgreeError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [groupError, setGroupError] = useState(false);
  // Bấm "Tạo lại máy": _id Image cũ, gửi kèm khi tạo để API xoá Image đó (không vướng imageAllowed).
  // Chọn thẻ gói khác (handlePick) thì coi là tạo mới, bỏ giá trị này.
  const [recreateImage, setRecreateImage] = useState("");

  // Máy đang boot (chưa có order): hiện "Đang khởi tạo" ở Máy đang thuê cho tới khi nhận socket boot_result.
  // pending: máy vừa tạo ở trình duyệt này; bootingImages: máy is_booting = 1 lấy từ server (giữ được khi tải lại trang)
  const [pending, setPending] = useState<ImageItem[]>([]);
  const [bootingImages, setBootingImages] = useState<ImageItem[]>([]);
  const pendingRef = useRef<ImageItem[]>([]);
  // Image đã nhận boot_result: không hiện lại "Đang khởi tạo" dù response createOrder / danh sách is_booting tới muộn
  const doneRef = useRef<Set<string>>(new Set());

  pendingRef.current = pending;

  // Tab đang được xem (khi bị ẩn ở trang Thuê máy tính thì ngừng hỏi lại trạng thái package)
  const activeRef = useRef(active);

  activeRef.current = active;

  // Hỏi lại trạng thái ngay (vòng hỏi 5s bên dưới gán hàm này), dùng khi vừa chuyển sang tab
  const pollNowRef = useRef<() => void>();

  const [update, forceUpdate] = useForceUpdate();
  const { subscribeOnce, subscribeUntilDestroy } = useObservable();

  const getMyInfo = () => {
    subscribeOnce(AccountService.getMyInfo(), (account) => {
      if (account) {
        setCurrentAccount(account);
        StorageService.setObject(CURRENT_ACCOUNT, account);
        dispatch(storeMyInfo(account));
      }
    });
  };

  useEffect(() => {
    getMyInfo();

    subscribeUntilDestroy(PackageService.getAllPackages(cloudPc), (items) => {
      setPackages(ofTab(items));
      setPackagesLoaded(true);
    });

    // Mỗi danh mục lấy package qua /packages/categories/:id như CreateImageModal cũ
    subscribeUntilDestroy(CategoryService.getCategories(), (categories) => {
      setCategories([...categories].sort(bySortOrder));
      categories.forEach((category) =>
        subscribeUntilDestroy(
          PackageService.getPackages(category._id, cloudPc),
          (items) =>
            setPackagesByCategory((pre) => ({ ...pre, [category._id]: ofTab(items) }))
        )
      );
    });
  }, []);

  useEffect(() => {
    const sub = SocketService.onBootResult().subscribe((data) => {
      const success = data?.statusCode === BootStatusCode.SUCCESS;
      const imageId = data?.imageId;

      // Tab Máy vật lý và Cloud PC cùng nghe boot_result và cùng hiện mọi máy: chỉ tab đang xem hiện thông báo,
      // tab còn lại chỉ bỏ máy "Đang khởi tạo" và tải lại danh sách
      const showResult = activeRef.current;

      const name = pendingRef.current.find((item) => item._id === imageId)?.name;

      if (imageId) doneRef.current.add(imageId);

      // Bỏ máy "Đang khởi tạo": thành công thì order hiện ra sau khi tải lại, thất bại thì backend đã xoá image
      setPending((list) => (imageId ? list.filter((item) => item._id !== imageId) : []));
      setBootingImages((list) => (imageId ? list.filter((item) => item._id !== imageId) : []));

      if (data?.message && showResult) {
        openPortalDialog(BootResultModal, { success, message: data.message, name })
          .afterClosed()
          .subscribe((res) => {
            if (res?.see) document.getElementById(rentedId)?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
      }

      // tải lại danh sách máy đang thuê và số dư (bị trừ khi boot, được hoàn khi lỗi)
      forceUpdate();
      getMyInfo();
    });

    return () => sub.unsubscribe();
  }, []);

  // Cập nhật Sẵn sàng / Hết máy mỗi 5s khi người dùng đang xem trang.
  // - Gọi nối tiếp (xong lần trước mới hẹn lần sau) nên không bao giờ chồng request
  // - Tab trình duyệt bị ẩn thì không gọi; mở lại tab thì cập nhật ngay
  // - Chỉ tạo object mới cho package có thay đổi, không đổi gì thì giữ nguyên state nên không render lại
  useEffect(() => {
    const INTERVAL = 5000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sub: Subscription | undefined;
    let inFlight = false;
    let stopped = false;

    const sameList = (a?: string[], b?: string[]) => (a || []).join("|") === (b || []).join("|");

    const apply = (list: Pick<Package, "_id" | "is_available" | "available_groups" | "orders_end_time">[]) => {
      const byId = new Map(list.map((item) => [String(item._id), item]));
      const patch = (pkg: Package) => {
        const next = byId.get(pkg._id);

        if (
          !next ||
          (next.is_available === pkg.is_available &&
            sameList(next.available_groups, pkg.available_groups) &&
            sameList(next.orders_end_time, pkg.orders_end_time))
        ) {
          return pkg;
        }

        return {
          ...pkg,
          is_available: next.is_available,
          available_groups: next.available_groups,
          orders_end_time: next.orders_end_time,
        };
      };
      const patchList = (items: Package[]) => {
        const next = items.map(patch);

        return next.some((pkg, i) => pkg !== items[i]) ? next : items;
      };

      setPackages((prev) => patchList(prev));
      setPackagesByCategory((prev) => {
        let changed = false;
        const next: Record<string, Package[]> = {};

        Object.entries(prev).forEach(([key, items]) => {
          next[key] = patchList(items);
          changed = changed || next[key] !== items;
        });

        return changed ? next : prev;
      });
    };

    const schedule = (ms = INTERVAL) => {
      if (!stopped) timer = setTimeout(tick, ms);
    };

    function tick() {
      if (stopped || inFlight) return;

      if (document.hidden || !activeRef.current) {
        schedule();

        return;
      }

      inFlight = true;
      // chỉ hỏi trạng thái của loại máy ở tab này (API chỉ tính phần đó)
      sub = PackageService.getAvailability(cloudPc).subscribe({
        next: (list) => {
          inFlight = false;
          apply(list || []);
          schedule();
        },
        // lỗi (mất mạng, 429…) thì thưa ra 15s rồi thử lại
        error: () => {
          inFlight = false;
          schedule(15000);
        },
      });
    }

    const onVisible = () => {
      if (!document.hidden && !inFlight) {
        clearTimeout(timer);
        tick();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisible);
    pollNowRef.current = onVisible;

    return () => {
      stopped = true;
      pollNowRef.current = undefined;
      clearTimeout(timer);
      sub?.unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Vừa chuyển sang tab này: cập nhật Sẵn sàng / Hết máy ngay, không đợi tới lượt hỏi 5s kế tiếp
  const wasActiveRef = useRef(active);

  useEffect(() => {
    // danh sách Máy đang thuê cũng tải lại: máy vừa tạo / gia hạn / xoá ở tab kia hiện ngay
    if (active && !wasActiveRef.current) {
      pollNowRef.current?.();
      forceUpdate();
    }

    wasActiveRef.current = active;
  }, [active]);

  // Máy đang boot trên server (vd: vừa tải lại trang khi máy còn đang khởi tạo)
  useEffect(() => {
    subscribeUntilDestroy(ImageService.getImage(DEFAULT_PAGE, true), (res) =>
      setBootingImages(
        (res?.data || []).filter(
          (image) => image.is_booting === 1 && !doneRef.current.has(image._id)
        )
      )
    );
  }, [update]);

  // Giờ hiện tại để máy hết giờ chuyển sang "Đã kết thúc" ngay trên giao diện như bản thiết kế
  // (server tắt máy và đổi status order bằng cron checkTimeOutOrder, 5 phút / lần, nên có thể trễ tới 5 phút)
  const [now, setNow] = useState(() => Date.now());
  const ordersRef = useRef<Order[]>([]);
  const listQueryRef = useRef({ page, orderStatus });

  ordersRef.current = orders;
  listQueryRef.current = { page, orderStatus };

  // Cập nhật "now" mỗi 30s và đúng lúc máy gần nhất hết giờ
  useEffect(() => {
    const ends = orders
      .filter((order) => order.status === OrderStatus.BUSY)
      .map((order) => getOrderEndTime(order).valueOf())
      .filter((end) => end > now);
    const next = ends.length ? Math.min(...ends) - Date.now() + 500 : Infinity;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(1000, Math.min(30000, next)));

    return () => clearTimeout(timer);
  }, [orders, now]);

  // Đồng bộ nền với server khi còn máy đang chạy: mỗi 60s lấy lại danh sách (im lặng, không loader),
  // chỉ thay state khi dữ liệu đổi để không nháy. Tạm dừng khi tab ẩn hoặc đang ở Cloud PC.
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let subs: Subscription[] = [];

    const schedule = () => {
      if (!stopped) timer = setTimeout(tick, 60000);
    };

    function tick() {
      const hasBusy = ordersRef.current.some((order) => order.status === OrderStatus.BUSY);

      if (stopped || document.hidden || !activeRef.current || !hasBusy) {
        schedule();

        return;
      }

      const { page: p, orderStatus: st } = listQueryRef.current;

      // lỗi ở dev trả NEVER (không gọi error) nên hẹn lần sau ngay, huỷ request cũ nếu còn treo
      subs.forEach((sub) => sub.unsubscribe());
      schedule();
      subs = [
        OrderService.getOrders(p, undefined, undefined, undefined, st, RENTED_FILTER, true).subscribe({
          next: (res) => {
            const next = (res.data || []).filter((order) => order.image);

            if (JSON.stringify(next) !== JSON.stringify(ordersRef.current)) {
              setOrders(next);
              setTotalPages(res.total_page);
            }
          },
        }),
        OrderService.getOrders(DEFAULT_PAGE, undefined, undefined, undefined, OrderStatus.BUSY, RENTED_FILTER, true).subscribe((res) =>
          setOrderCount((pre) => (pre.busy === (res.count || 0) ? pre : { ...pre, busy: res.count || 0 }))
        ),
        OrderService.getOrders(DEFAULT_PAGE, undefined, undefined, undefined, OrderStatus.FINISHED, RENTED_FILTER, true).subscribe((res) =>
          setOrderCount((pre) => (pre.finished === (res.count || 0) ? pre : { ...pre, finished: res.count || 0 }))
        ),
      ];
    }

    schedule();

    return () => {
      stopped = true;
      clearTimeout(timer);
      subs.forEach((sub) => sub.unsubscribe());
    };
  }, []);

  // Máy đang thuê: lấy danh sách order như trang Quản lý đơn hàng
  useEffect(() => {
    subscribeUntilDestroy(
      OrderService.getOrders(page, undefined, undefined, undefined, orderStatus, RENTED_FILTER),
      (res) => {
        setOrdersLoaded(true);
        // Bỏ các order có image đã bị xoá
        setOrders((res.data || []).filter((order) => order.image));
        setTotalPages(res.total_page);
      }
    );
  }, [update, page, orderStatus]);

  // Số đếm cho bộ lọc Đang chạy / Đã kết thúc
  useEffect(() => {
    subscribeUntilDestroy(OrderService.getOrders(DEFAULT_PAGE, undefined, undefined, undefined, OrderStatus.BUSY, RENTED_FILTER), (res) =>
      setOrderCount((pre) => ({ ...pre, busy: res.count || 0 }))
    );
    subscribeUntilDestroy(OrderService.getOrders(DEFAULT_PAGE, undefined, undefined, undefined, OrderStatus.FINISHED, RENTED_FILTER), (res) =>
      setOrderCount((pre) => ({ ...pre, finished: res.count || 0 }))
    );
  }, [update]);

  const handleOrderStatusChange = (status?: OrderStatus) => {
    setOrderStatus(status);
    setPage(DEFAULT_PAGE);
  };

  /* ---------- tạo máy: createImage rồi createOrder ---------- */
  const handlePick = (item: Package | null) => {
    setSelectedPackage(item || undefined);
    setRecreateImage("");
    // tên máy để khách tự nhập, không điền sẵn tên cấu hình
    setImageName("");
    setNameError(false);
    setGroupError(false);
    setAgree(false);
    setAgreeError(false);
    // chỉ chọn sẵn khi gói có đúng 1 hệ điều hành còn máy, từ 2 bản trở lên để khách tự chọn
    setGroup(defaultGroup(item || undefined));
    setUnit(item?.user_day ? BootImageUnit.DAY : BootImageUnit.HOUR);
    setAmount(item?.user_day ? item.numbers_of_day : 1);
  };

  const handleCreateImage = () => {
    if (!selectedPackage) return;

    if (!imageName.trim()) {
      setNameError(true);
      addToast({ text: "Vui lòng nhập tên máy", status: "warn" });

      return;
    }

    // group và os lấy từ hệ điều hành người dùng chọn (tách từ Package.group)
    const os = parsePackageGroup(selectedPackage.group).find(
      (item) => item.group === group
    )?.os;

    if (!group || !os) {
      setGroupError(true);
      addToast({ text: "Vui lòng chọn hệ điều hành", status: "warn" });

      return;
    }

    if (!agree) {
      setAgreeError(true);
      addToast({ text: "Vui lòng tích đồng ý điều khoản sử dụng trước khi tạo máy", status: "warn" });

      return;
    }

    if (!isGroupAvailable(livePackage, group)) {
      addToast({ text: `${os} đang tạm hết máy, vui lòng chọn hệ điều hành khác`, status: "warn" });

      return;
    }

    if (!currentAccount) {
      addToast({ text: "Vui lòng đăng nhập lại để sử dụng tính năng này" });

      return;
    }

    setBusy(true);

    // Cloud PC: 1 request (API trừ tiền, tạo Image đang khởi tạo rồi tạo VM ở nền), kết quả báo qua socket boot_result
    if (cloudPc) {
      HttpService.local(() =>
        CloudPcService.create({
          name: imageName.trim(),
          group,
          os,
          id_package: selectedPackage._id,
          rental_period: amount,
          unit,
          recreate_image: recreateImage || undefined,
        })
      ).subscribe({
        next: (image) => {
          if (!doneRef.current.has(image._id)) {
            setPending((list) => [{ ...image, package: selectedPackage, is_booting: 1 } as ImageItem, ...list]);
          }

          // tạo lại máy: API đã xoá Image cũ, tải lại để bỏ dòng cũ khỏi Máy đang thuê
          if (recreateImage) forceUpdate();

          document.getElementById(rentedId)?.scrollIntoView({ behavior: "smooth", block: "start" });
          handlePick(null);
          setBusy(false);
          // số dư đã bị trừ (được hoàn nếu tạo thất bại)
          getMyInfo();
        },
        error: () => setBusy(false),
      });

      return;
    }

    // Bước 1: tạo PC (createImage). Bước 2: boot ngay (createOrder), giống bấm "Boot" ở bản cũ.
    HttpService.local(() =>
      ImageService.createImage({
        name: imageName.trim(),
        group,
        os,
        id_package: selectedPackage._id,
        rental_period: amount,
        unit,
        recreate_image: recreateImage || undefined,
      })
    ).subscribe({
      next: (image) => {
        // createImage trả về Image (chưa populate package): ghép thêm gói và thông tin vừa chọn để hiện như 1 ImageItem
        const bootingImage = {
          ...image,
          group,
          os,
          rental_period: amount,
          unit,
          package: selectedPackage,
          is_booting: 1,
        } as unknown as ImageItem;

        // Hiện máy ngay ở "Máy đang thuê" với trạng thái Đang khởi tạo (trước khi gọi createOrder)
        setPending((list) => [bootingImage, ...list]);
        document.getElementById(rentedId)?.scrollIntoView({ behavior: "smooth", block: "start" });

        // createOrder bị từ chối (hết máy, không đủ số dư…) thì bỏ máy khỏi danh sách.
        // Nghe HttpService.onError$ vì ở môi trường dev HttpService trả NEVER khi lỗi nên nhánh error không chạy.
        let dropped = false;
        const dropPending = () => {
          if (dropped) return;
          
          dropped = true;
          setPending((list) => list.filter((item) => item._id !== image._id));
          setBusy(false);
          // Image vừa tạo không có đơn: xoá ngầm, nếu không nó vẫn tính vào giới hạn imageAllowed mà không hiện ở bảng
          ImageService.deleteImage(image._id, { silent: true }).subscribe({ complete: forceUpdate });
        };
        const errorSub = HttpService.onError$.subscribe((err) => {
          if (err?.request?.method === "POST" && String(err?.request?.url || "").endsWith("/orders")) {
            errorSub.unsubscribe();
            dropPending();
          }
        });

        const createOrderRequest: CreateOrderRequest = {
          image: image._id,
          rental_period: amount,
          unit,
        };

        HttpService.local(() => OrderService.createOrder(createOrderRequest)).subscribe({
          next: () => {
            errorSub.unsubscribe();
            handlePick(null);
            setBusy(false);
            getMyInfo();
            forceUpdate();
          },
          error: () => {
            // Không boot được: lỗi đã được HttpService báo bằng toast
            errorSub.unsubscribe();
            dropPending();
            forceUpdate();
          },
        });
      },
      error: () => setBusy(false),
    });
  };

  /** Máy đã kết thúc: điền lại gói, tên và hệ điều hành cũ vào "Thông tin tạo máy" */
  const handleRecreate = (order: Order) => {
    const image = order.image;

    // máy thuộc loại của tab kia (Máy vật lý / Cloud PC): chuyển sang tab đó để điền cấu hình
    if (!!image?.cloud_pc !== cloudPc) {
      onRecreateOther?.(order);

      return;
    }

    // Trạng thái còn/hết máy lấy theo danh sách package mới nhất
    const pkg =
      Object.values(packagesByCategory)
        .flat()
        .find((item) => item._id === image.package._id) ||
      packages.find((item) => item._id === image.package._id);

    if (!pkg?.is_available) {
      addToast({
        text: `${image.package.name} đang hết máy, vui lòng xem Lịch sẵn sàng`,
        status: "warn",
      });

      return;
    }

    const options = parsePackageGroup(pkg.group);
    const oldGroup =
      options.find((item) => item.group === order.group)?.group ||
      options.find((item) => item.group === image.group)?.group ||
      options.find((item) => item.os === image.os)?.group;

    handlePick(pkg);
    setRecreateImage(image._id);
    // máy tạo trước khi có giới hạn có thể có tên dài hơn: cắt cho khớp API
    setImageName((image.name || "").slice(0, MAX_LEN.NAME));

    // hệ điều hành cũ đang hết máy thì báo cho khách: còn 1 bản thì đã chọn sẵn bản đó, từ 2 bản thì để khách chọn
    if (oldGroup && isGroupAvailable(pkg, oldGroup)) {
      setGroup(oldGroup);
    } else if (oldGroup) {
      addToast({
        text: `${options.find((item) => item.group === oldGroup)?.os} đang tạm hết máy, ${
          defaultGroup(pkg) ? "đã chọn hệ điều hành khác" : "vui lòng chọn hệ điều hành khác"
        }`,
        status: "warn",
      });
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const recreateAtRef = useRef(0);

  useEffect(() => {
    if (!recreate || !packagesLoaded || recreate.at === recreateAtRef.current) return;

    recreateAtRef.current = recreate.at;
    handleRecreate(recreate.order);
  }, [recreate, packagesLoaded]);

  /* ---------- thao tác trên máy: giữ nguyên như GpuHub / ComputerCard cũ ---------- */
  const handleExtendClick = (order: Order) => {
    if (!currentAccount) {
      addToast({ text: "Vui lòng đăng nhập lại để sử dụng tính năng này" });

      return;
    }

    openPortalDialog(BootImageModal, { order, currentAccount, color: colorOf(order.image?.package) })
      .afterClosed()
      .subscribe((data) => {
        // Thiếu tiền: sang trang Nạp tiền với số tiền còn thiếu
        if (data?.topup) {
          navigate(`${PathnameUrl.RECHARGE}?amount=${Math.ceil(data.topup)}&from=extend`);

          return;
        }

        if (data?.rentalPeriod && data?.unit) {
          const createOrderRequest: CreateOrderRequest = {
            image: order.image._id,
            rental_period: data.rentalPeriod,
            unit: data.unit,
          };

          run(`extend:${order.image._id}`, () => OrderService.extendOrder(createOrderRequest), () => {
            addToast({ text: "Gia hạn thành công" });
            // cập nhật giờ kết thúc trong bảng và số dư
            getMyInfo();
            forceUpdate();
          });
        }
      });
  };

  const handleDeleteImage = (imageId: string) => {
    openPortalDialog(ConfirmModal, {
      title: "Xoá máy",
      message: "Bạn có chắc chắn xóa PC này không?",
      confirmLabel: "Xoá máy",
      danger: true,
    })
      .afterClosed()
      .subscribe((data) => {
        if (data?.isAccept) {
          run(`delete:${imageId}`, () => ImageService.deleteImage(imageId), () => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
  };

  const onConnectImageClick = (image: ImageItem) => {
    const imageId = image._id;

    // RDP theo loại máy của dòng (bảng có cả máy vật lý và Cloud PC)
    run(`rdp:${imageId}`, () => (image.cloud_pc ? CloudPcService.getRDPFile(imageId) : OrderService.getRDPFile(imageId)), (data) => {
      const contentDisposition = data.responseHeaders["content-disposition"];
      const fileNameOriginal = contentDisposition?.split("filename=")?.[1];

      if (fileNameOriginal) {
        const fileName = fileNameOriginal.substring(
          1,
          fileNameOriginal.length - 2
        );

        if (fileName) {
          downloadFile(data.response, "application/rdp", fileName);
        }
      }
    });
  };

  // Bật máy Cloud PC (lỡ shutdown trong Windows / tạo xong máy chưa bật): không hỏi lại, API luôn báo thành công
  const onStartCloudPc = (image: ImageItem) => {
    run(`start:${image._id}`, () => CloudPcService.start(image._id), (data) =>
      addToast({ text: data.message || "Bật máy thành công" })
    );
  };

  const onPlayPcrender = (image: ImageItem) => {
    // Play Game cũng chạy trên IP nội bộ của máy
    if (image.cloud_pc && noIntIp(image)) return;

    openPortalDialog(PlayPcrenderModal, { image })
      .afterClosed()
      .subscribe((data) => {
        if (data?.token) {
          forceUpdate();
        }
      });
  };

  const handleStartClick = (image: ImageItem) => {
    openPortalDialog(ConfirmModal, {
      title: "Reset máy",
      confirmLabel: "Reset máy",
      message: image.cloud_pc
        ? "Máy sẽ khởi động lại, các chương trình đang chạy sẽ bị tắt."
        : "Việc RESET máy có thể làm mất toàn bộ dữ liệu đã cài đặt nếu máy chưa được mở băng ổ cứng.",
    })
      .afterClosed()
      .subscribe((res) => {
        if (res?.isAccept && image.cloud_pc) {
          // Cloud PC: restart VM
          run(`reset:${image._id}`, () => CloudPcService.restart(image._id), (data) => addToast({ text: data.message }));

          return;
        }

        if (res?.isAccept) {
          run(`reset:${image._id}`, () => OrderService.reset(image.int_ip_address), (data) => {
            if (data.message) {
              addToast({ text: data.message });
              forceUpdate();
            }
          });
        }
      });
  };

  const onReConnect = (imageId: string) => {
    run(`pw:${imageId}`, () => ImageService.reConnect(imageId), (res) => {
      if (res.statusCode === 200) {
        // Mật khẩu mới đã lưu vào đơn hàng (order.passRdp): hiện ngay ở cột Đăng nhập / IP remote, tô xanh, rồi tải lại danh sách
        const order = orders.find((item) => item.image?._id === imageId && item.status === OrderStatus.BUSY);

        if (order && res.passRdp) {
          setOrders((pre) => pre.map((item) => (item._id === order._id ? { ...item, passRdp: res.passRdp } : item)));
          setPwNew(order._id);
          clearTimeout(pwTimer.current);
          pwTimer.current = setTimeout(() => setPwNew(""), 2500);
        }

        addToast({
          text: res.passRdp
            ? `Đã đổi mật khẩu ${order?.image?.name || ""}: ${res.passRdp}`.replace(" :", ":")
            : res.message || "Đổi mật khẩu thành công",
        });
        forceUpdate();
      } else {
        addToast({
          text: res.message || "Lỗi tạo kết nối lại, vui lòng thử lại sau 2 phút, không được thì liên hệ admin",
          status: "inValid",
        });
      }
    });
  };

  // Đổi đường truyền / IP xoay / Tunnel chạy trên IP nội bộ của máy (Cloud PC lấy từ máy chủ Cloud PC khi tạo máy)
  const noIntIp = (image: ImageItem) => {
    if (image.int_ip_address) return false;

    addToast({ text: "Máy này chưa có IP nội bộ, hãy liên hệ Admin!", status: "warn" });

    return true;
  };

  const handleChooseRouter = (image: ImageItem) => {
    if (noIntIp(image)) return;

    openPortalDialog(WanModal, image)
      .afterClosed()
      .subscribe((data) => {
        if (data?.wan) {
          const sshCliRequest: SshCli = {
            int_ip_address: image.int_ip_address,
            wan: data.wan.trim(),
            ssh_id: image.ssh?._id,
            image_id: image._id,
          };

          const wanChoice = data.wan.split(" - ")[0];

          run(
            `wan:${image._id}`,
            () =>
              wanChoice.toUpperCase() === "WAN00"
                ? SshService.sshCliDeleteMangle(sshCliRequest)
                : SshService.sshCliAddMangle(sshCliRequest),
            (result) => addToast({ text: result.message })
          );
        }
      });
  };

  const handleChooseIpXoay = (image: ImageItem) => {
    if (noIntIp(image)) return;

    openPortalDialog(IPXoayModal, image)
      .afterClosed()
      .subscribe((data) => {
        if (data?.wan) {
          const sshCliRequest: SshCli = {
            int_ip_address: image.int_ip_address,
            wan: data.wan.trim(),
            ssh_id: image.ssh?._id,
            image_id: image._id,
          };

          run(
            `ip:${image._id}`,
            () =>
              data?.change_ip
                ? SshService.sshCliChangeIPXoay(sshCliRequest)
                : SshService.sshCliStartIPXoay(sshCliRequest),
            (result) => {
              addToast({ text: result.message || (data?.change_ip ? "Thành Công!" : "Start Thành Công!") });
              forceUpdate();
            }
          );
        }
      });
  };

  const handleChoosePcrenderTunnel = (image: ImageItem) => {
    if (noIntIp(image)) return;

    openPortalDialog(TunnelModal, image)
      .afterClosed()
      .subscribe((data) => {
        if (!data?.os) return;

        if (!image.ssh?.api_tunnel) {
          addToast({ text: "Máy này chưa có API Tunnel, hãy liên hệ Admin!", status: "warn" });

          return;
        }

        const tunnelRequest: Tunnel = {
          name: image.account.username,
          allowIp: image.int_ip_address + "/32",
          os: data.os,
          image_id: image._id,
          api_tunnel: image.ssh.api_tunnel,
        };

        run(`tunnel:${image._id}`, () => SshService.chooseTunnel(tunnelRequest), (data1) => {
          if (data.os === "mobile") {
            openPortalDialog(QRCodeModal, data1);

            return;
          }

          const contentDisposition =
            data1.responseHeaders["content-disposition"];
          const fileNameOriginal = contentDisposition?.split("filename=")?.[1];

          if (fileNameOriginal) {
            const fileName = fileNameOriginal.substring(
              1,
              fileNameOriginal.length - 2
            );

            if (fileName) {
              downloadFile(data1.response, "application/exe", fileName);
            }

            const logRequest: LogRequest = {
              message: fileName
                ? `Pcrender Tunnel - Tải thành công file ${fileName}`
                : `Pcrender Tunnel - Tải file exe KHÔNG thành công ${fileName}`,
            };

            subscribeOnce(LogService.createLogByUser(logRequest), () => {});
          }
        });
      });
  };

  return (
    // khoảng cách giữa phần chọn cấu hình và Máy đang thuê như .content (gap 24px) của bản thiết kế
    <div className="flex flex-col gap-6 max-[760px]:gap-[18px]">
      <div className="grid grid-cols-[minmax(0,1fr)_310px] items-stretch gap-5 max-[1240px]:grid-cols-[minmax(0,1fr)_300px] max-[1100px]:grid-cols-1">
        <ConfigCatalog
          cloudPc={cloudPc}
          loading={!packagesLoaded}
          items={packages}
          itemsByCategory={packagesByCategory}
          categories={categories}
          discount={currentAccount?.discount_pc || 0}
          selectedId={selectedPackage?._id || ""}
          onPick={(item) => handlePick(item as Package | null)}
        />
        <CreateSummary
          pkg={livePackage}
          discount={currentAccount?.discount_pc || 0}
          balance={currentAccount?.balance || 0}
          name={imageName}
          group={group}
          onGroup={setGroup}
          unit={unit}
          amount={amount}
          busy={busy}
          onName={setImageName}
          onUnit={setUnit}
          onAmount={setAmount}
          onUnpick={() => handlePick(null)}
          onCreate={handleCreateImage}
          onTopup={(need) => navigate(`${PathnameUrl.RECHARGE}?amount=${Math.ceil(need)}&from=rent`)}
          color={colorOf(selectedPackage)}
          agree={agree}
          agreeError={agreeError && !agree}
          nameError={nameError && !imageName.trim()}
          groupError={groupError}
          anchorId={cloudPc ? "rent-summary-cloud" : "rent-summary"}
          onAgree={(v) => {
            setAgree(v);

            if (v) setAgreeError(false);
          }}
        />
      </div>

      <RentedTable
        anchorId={rentedId}
        now={now}
        loading={!ordersLoaded}
        colorOf={colorOf}
        pwNew={pwNew}
        orders={orders}
        booting={[...pending, ...bootingImages.filter((image) => !pending.some((item) => item._id === image._id))]}
        orderStatus={orderStatus}
        orderCount={orderCount}
        onOrderStatusChange={handleOrderStatusChange}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRecreate={handleRecreate}
        onConnect={onConnectImageClick}
        onPlay={onPlayPcrender}
        onExtend={handleExtendClick}
        onDelete={handleDeleteImage}
        onRouter={handleChooseRouter}
        onIpXoay={handleChooseIpXoay}
        onTunnel={handleChoosePcrenderTunnel}
        onReset={handleStartClick}
        onStart={onStartCloudPc}
        busy={rowBusy}
        // dòng Cloud PC không có Đổi mật khẩu (rented-table ẩn mục này theo image.cloud_pc)
        onReConnect={onReConnect}
      />
    </div>
  );
}

export default PhysicalTab;
